import { saveToStorage } from '../helpers/storage';
import createDataProvider from './createDataProvider';
import { providerContextReducer } from './providerContextReducer';

const initialState = {
  form: {
    data: {},
    initialized: false,
    hasUnsavedChanges: false,
    isSubmitting: false,
    triggerSubmit: false,
    triggerDiscard: false,
    containerWidth: null,
    errors: {}
  },
  itemStorage: { value: [], loading: false, error: null },
  setupGuide: { showSidebar: false, activeSidebarStep: null },
  vendorCatalog: {
    initialized: false,
    isDemoMode: false,
    showDrawer: false,
    showInputModal: false,
    skipInputModalOnAdd: false,
    inputProducts: [],
    fetchingProducts: [],
    showMinimumQuantityModal: false,
    selectedInputProductUuid: null,
    showDemoWarning: false,
    hasInitializedDemoWarning: false,
    removeProductModal: {
      isOpen: false,
      ecomVendorProducts: []
    }
  },
  webstore: {
    changeShippingModal: { isOpen: false },
    addProductToCartModal: {
      isOpen: false,
      ecomStoreProduct: null
    },
    previewWebstoreProductModal: {
      isOpen: false,
      ecomStoreProduct: null
    },
    checkoutSummaryDrawer: {
      isOpen: false,
      showProductCustomization: false,
      ecomStoreCartProduct: null,
      ecomStoreCartPackage: null
    },
    continueAsGuestModal: {
      isOpen: false,
      formData: null,
      hasBeenShown: false
    },
    socialSharingModel: { isOpen: false, shareUrl: null, isResourceUrl: false },
    shippingAddress: null
  }
};

const setFormData = (dispatch) => async ({
  data,
  containerWidth,
  saveButtonText = null,
  hideDiscardButton = false,
  getIsVisible = null,
  hasUnsavedChanges = true,
  triggerDiscard = false,
  loading = false
}) => {
  dispatch({
    type: 'success',
    stateName: 'form',
    payload: {
      data,
      hasUnsavedChanges,
      containerWidth,
      saveButtonText,
      getIsVisible,
      hideDiscardButton,
      triggerDiscard,
      isSubmitting: loading,
      initialized: true,
      triggerSubmit: false
    }
  });
};

const triggerFormDiscard = (dispatch) => async () => {
  dispatch({
    type: 'success',
    stateName: 'form',
    payload: { triggerDiscard: true }
  });
};

const triggerFormSubmit = (dispatch) => async (triggerSubmit = true) => {
  dispatch({
    type: 'success',
    stateName: 'form',
    payload: { triggerSubmit }
  });
};

const resetForm = (dispatch) => async () => {
  dispatch({
    type: 'success',
    stateName: 'form',
    payload: {
      data: {},
      hasUnsavedChanges: false,
      loading: false,
      isSubmitting: false,
      triggerSubmit: false,
      triggerDiscard: false,
      initialized: false,
      errors: {}
    }
  });
};

const toggleSetupGuideSidebar = (dispatch) => async (
  open = false,
  activeSidebarStep = null
) => {
  saveToStorage('showSetupGuide', open ? 'true' : 'false');
  saveToStorage('activeSidebarStep', activeSidebarStep?.toString() ?? '');
  dispatch({
    type: 'success',
    stateName: 'setupGuide',
    payload: { showSidebar: open, activeSidebarStep }
  });
};

const toggleVendorCatalogDemoMode = (dispatch) => async (
  pkEcomStore,
  isDemoMode = false
) => {
  saveToStorage(
    `vendor-catalog-demo-mode-${pkEcomStore}`,
    isDemoMode ? 'true' : 'false'
  );
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: { isDemoMode, initialized: true, showDemoWarning: false }
  });
};

const toggleVendorCatalogDemoWarning = (dispatch) => async (showWarning) => {
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: { showDemoWarning: showWarning, hasInitializedDemoWarning: true }
  });
};

const toggleVendorCatalogDrawer = (dispatch) => async (showDrawer = false) => {
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: { showDrawer }
  });
};

const toggleVendorCatalogInputModal = (dispatch) => async (
  showInputModal = false,
  selectedInputProductUuid = null
) => {
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: selectedInputProductUuid
      ? { showInputModal, selectedInputProductUuid }
      : { showInputModal }
  });
};

const toggleVendorCatalogSkipInputModal = (dispatch) => async (
  skipInputModalOnAdd = false
) => {
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: { skipInputModalOnAdd }
  });
};

const setVendorCatalogInputProducts = (dispatch) => async ({
  inputProducts = [],
  showInputModal = false,
  selectedInputProductUuid = null,
  showMinimumQuantityModal = false
} = {}) => {
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: {
      inputProducts,
      showInputModal,
      selectedInputProductUuid,
      showMinimumQuantityModal
    }
  });
};

const setVendorCatalogRemoveStoreProductModal = (dispatch) => async ({
  isOpen,
  ecomVendorProducts
} = {}) => {
  dispatch({
    type: 'success',
    stateName: 'vendorCatalog',
    payload: {
      removeProductModal: {
        isOpen,
        ecomVendorProducts
      }
    }
  });
};

const appendItemStorage = (dispatch) => async (items) => {
  dispatch({
    type: 'append',
    stateName: 'itemStorage',
    payload: { value: items }
  });
};

const removeItemStorage = (dispatch) => async ({ filter } = {}) => {
  dispatch({
    type: 'remove',
    stateName: 'itemStorage',
    payload: filter
  });
};

const toggleWebstoreAddProductToCartModal = (dispatch) => async ({
  isOpen,
  ecomStoreProduct
}) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: { addProductToCartModal: { isOpen, ecomStoreProduct } }
  });
};

const toggleWebstorePreviewProductModal = (dispatch) => async ({
  isOpen,
  ecomStoreProduct
}) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: { previewWebstoreProductModal: { isOpen, ecomStoreProduct } }
  });
};

const toggleWebstoreChangeShippingModal = (dispatch) => async ({ isOpen }) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: { changeShippingModal: { isOpen } }
  });
};

const toggleWebstoreCheckoutSummaryDrawer = (dispatch) => async ({
  isOpen,
  showProductCustomization = false,
  ecomStoreCartProduct = null,
  ecomStoreCartPackage = null
}) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: {
      checkoutSummaryDrawer: {
        isOpen,
        showProductCustomization,
        ecomStoreCartProduct,
        ecomStoreCartPackage
      }
    }
  });
};

const toggleWebstoreContinueAsGuestModal = (dispatch) => async ({
  isOpen,
  formData = null
}) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: {
      continueAsGuestModal: {
        isOpen,
        formData,
        hasBeenShown: true
      }
    }
  });
};

const setWebstoreShippingCosts = (dispatch) => async ({
  value = [],
  loading = false,
  error = null
}) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: { shippingCosts: { value, loading, error } }
  });
};

const setWebstoreShippingAddress = (dispatch) => async (
  ecomCustomerAddress
) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: { shippingAddress: ecomCustomerAddress }
  });
};

const toggleWebstoreSocialSharingModel = (dispatch) => async ({
  isOpen = false,
  shareUrl = null,
  isResourceUrl = false
}) => {
  dispatch({
    type: 'success',
    stateName: 'webstore',
    payload: { socialSharingModel: { isOpen, shareUrl, isResourceUrl } }
  });
};

export const { Provider, Context } = createDataProvider(
  providerContextReducer,
  {
    setFormData,
    triggerFormDiscard,
    triggerFormSubmit,
    resetForm,
    toggleSetupGuideSidebar,
    toggleVendorCatalogDemoMode,
    toggleVendorCatalogDemoWarning,
    toggleVendorCatalogDrawer,
    toggleVendorCatalogInputModal,
    toggleVendorCatalogSkipInputModal,
    toggleWebstoreAddProductToCartModal,
    toggleWebstorePreviewProductModal,
    toggleWebstoreCheckoutSummaryDrawer,
    toggleWebstoreContinueAsGuestModal,
    toggleWebstoreChangeShippingModal,
    toggleWebstoreSocialSharingModel,
    setWebstoreShippingAddress,
    setWebstoreShippingCosts,
    setVendorCatalogRemoveStoreProductModal,
    setVendorCatalogInputProducts,
    appendItemStorage,
    removeItemStorage
  },
  initialState
);
